import { Component, ElementRef, Input, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Message, ExecuteActionEvent, User, SendMessageEvent } from '@progress/kendo-angular-conversational-ui';
import { TextAreaComponent } from '@progress/kendo-angular-inputs';
import { SVGIcon, xCircleIcon, imageIcon, imageAddIcon, commentIcon, gearsIcon } from '@progress/kendo-svg-icons';
import { ORD_Communication, PatchOrderCommunictionDto } from '../../interfaces/request.interface';
import { Store } from '@ngxs/store';
import { RequestActions } from '../../store/action/request.action';
import { AccountState } from '../../store/state/account.state';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { UserViewModel } from '../../interfaces/viewmodels/userviewmodel';
import { SignalRService } from '../../services/signalr.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChatUser, MentionMessage, extractMentionedUserIds, replaceMentionsWithHtml } from './chat-mention.models';

declare var CURRENT_USER_ID:number;

@Component({
    selector: 'app-communication-data',
    templateUrl: './communication-data.component.html',
    styleUrl: './communication-data.component.css',
    standalone: false
})
export class CommunicationDataComponent implements OnInit, OnDestroy {
  @Input() _initialcommunications?: ORD_Communication[];
  @Input() draftId!: number;
  @ViewChild("messageBoxInput", { static: false })
  public messageBoxInput!: TextAreaComponent;

  @ViewChild("uploadInput", { static: false })
  public inputFile!: ElementRef;

  @ViewChild("chatTextarea", { static: false })
  public chatTextarea!: ElementRef<HTMLTextAreaElement>;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private signalRService: SignalRService,
    private sanitizer: DomSanitizer
  ) {
    this.users$ = this.store.select(AccountState.users).pipe(
      map(dict =>
        Object.values(dict).map(vm => ({
          id:   vm.id,
          name: vm.displayName
        }))
      )
    );
  }
  
  public innerInputFocus = false;
  public selectedImage!: File;
  public xCircleIcon: SVGIcon = xCircleIcon;
  public imageIcon: SVGIcon = imageIcon;
  public messageText: string = '';
  public currentUserId: number = CURRENT_USER_ID;
  public users: User[] = [];
  public textarea!: ElementRef;
  public users$: Observable<{ id: number; name: string }[]>;
  
  // --- Mention state ---
  public allChatUsers: ChatUser[] = [];
  public filteredMentionUsers: ChatUser[] = [];
  public showMentionDropdown = false;
  public mentionSearchTerm = '';
  /** Character index in messageText where the triggering '@' was typed */
  private mentionTriggerIndex = -1;

  public id!: string;
  public designReferenceNumber!:number;
  public imageAddSVG: SVGIcon = imageAddIcon;
  public CommentSVG: SVGIcon = commentIcon;
  public GearsSVG: SVGIcon = gearsIcon;
  public viewChat: boolean = false;
  public windowTop = 100;
  public windowLeft = 50;
  public _communications: Message[]=[];
  private messageId = 0;

  // ─── Mention helpers ─────────────────────────────────────────

  /**
   * Called on every keyup in the custom textarea.
   * Detects '@' trigger, filters the user list, and closes
   * the dropdown when appropriate.
   */
  public onMentionKeyup(event: KeyboardEvent): void {
    const el = this.chatTextarea?.nativeElement;
    if (!el) { return; }

    const cursorPos = el.selectionStart ?? 0;
    const textBeforeCursor = this.messageText.substring(0, cursorPos);

    // Find the last '@' before cursor that is NOT inside an existing token
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      this.closeMentionDropdown();
      return;
    }

    // Ensure '@' is at start of input or preceded by a space / newline
    if (lastAtIndex > 0 && !/\s/.test(textBeforeCursor[lastAtIndex - 1])) {
      this.closeMentionDropdown();
      return;
    }

    // If there's a ']' between the '@' and the cursor we're past a completed token
    const between = textBeforeCursor.substring(lastAtIndex);
    if (between.includes(']')) {
      this.closeMentionDropdown();
      return;
    }

    // Extract the search fragment after '@'
    const searchTerm = textBeforeCursor.substring(lastAtIndex + 1);

    // Close on whitespace right after @  (user pressed space/enter)
    if (/\s/.test(searchTerm) && searchTerm.trim().length === 0) {
      this.closeMentionDropdown();
      return;
    }

    this.mentionTriggerIndex = lastAtIndex;
    this.mentionSearchTerm = searchTerm;

    // Filter users (show all when search is empty)
    this.filteredMentionUsers = this.allChatUsers.filter(u =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    this.showMentionDropdown = this.filteredMentionUsers.length > 0;
  }

  /**
   * Called when the user selects an item from the mention dropdown.
   */
  public onMentionSelect(user: ChatUser): void {
    if (this.mentionTriggerIndex < 0) { return; }

    const el = this.chatTextarea?.nativeElement;
    const cursorPos = el ? el.selectionStart ?? this.messageText.length : this.messageText.length;

    const before = this.messageText.substring(0, this.mentionTriggerIndex);
    const after  = this.messageText.substring(cursorPos);
    const token  = `@[${user.id}:${user.name}] `;

    this.messageText = before + token + after;
    this.closeMentionDropdown();

    // Restore cursor position after the inserted token
    setTimeout(() => {
      if (el) {
        const newPos = before.length + token.length;
        el.focus();
        el.setSelectionRange(newPos, newPos);
      }
    });
  }

  public closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.filteredMentionUsers = [];
    this.mentionTriggerIndex = -1;
    this.mentionSearchTerm = '';
  }

  // ─── Keyboard handling in custom message box ─────────────────

  public onCustomKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.showMentionDropdown) {
      this.closeMentionDropdown();
      event.preventDefault();
      return;
    }

    const isEnter = event.key === 'Enter';
    if (!isEnter) { return; }

    // Shift+Enter => newline
    if (event.shiftKey) { return; }

    // If mention dropdown is open, ignore Enter (let user pick from list)
    if (this.showMentionDropdown) {
      event.preventDefault();
      return;
    }

    // Plain Enter => send
    event.preventDefault();
    this.sendMessage();
  }

  // ─── Rendering mentions in message bubbles ───────────────────

  /**
   * Converts raw message text containing @[id:name] tokens into
   * sanitized HTML with styled <span> tags.
   */
  public parseMentions(text: string | undefined): SafeHtml {
    if (!text) {
      return '';
    }
    const html = replaceMentionsWithHtml(text);
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // ─── Original handlers (updated) ────────────────────────────

  public onKeyDown(e: KeyboardEvent): void {
    const isEnter = e.keyCode === 13;

    if (!isEnter) {
      return;
    }

    const newLine = e.metaKey || e.ctrlKey;
    const enterOnly = !(e.shiftKey || e.metaKey || e.ctrlKey);

    if (enterOnly && this.innerInputFocus) {
      e.preventDefault();
    }
    if (newLine) {
      this.messageBoxInput.value += `\r\n`;
    }
  }
  public clearValue(): void {
    if (!this.innerInputFocus) {
      this.messageText = '';
      this.innerInputFocus = true;
    }
  }
    public onFileSelect(event: Event): void {
      const target = event.target as HTMLInputElement;
      if (target && target.files && target.files[0]) {
        this.selectedImage = target.files[0];
      }
      const reader = new FileReader();
  
      const echo: Message = {
        id: this.nextMessageId(),
        text: `Now thats a great image!`,
        author: { id: 0, name: "FileVault Bot"},
      };
  
      reader.readAsDataURL(this.selectedImage);
      reader.onloadend = (data) => {
        const message: Message = {
          id: this.nextMessageId(),
          text: "",
          author: this.getCurrentUser(),
          attachments: [
            {
              contentType: "image/*",
              content: data.target ? data.target.result : null,
            },
          ],
        };
        this._communications = [...this._communications, message, echo];
      };
    }
    public onAction(e: ExecuteActionEvent): void {
      if (e.action.type === "reply") {
        this.inputFile.nativeElement.click();
      }
    }
    public onSendMessage(e: SendMessageEvent): void {
      const newMessage = e.message?.text;
      if (!newMessage) {
        return;
      }

      const timestamp = new Date();
      const mentionedUserIds = extractMentionedUserIds(newMessage);

      const message: MentionMessage = {
        id: this.nextMessageId(),
        text: newMessage,
        author: this.getCurrentUser(),
        timestamp: timestamp,
        mentionedUserIds: mentionedUserIds
      };
      this._communications = [...this._communications, message];

      this.store.dispatch(new RequestActions.PostCommunication({
              key: this.draftId,
              communication: {
              userId: CURRENT_USER_ID,
              message: newMessage,
              mentionedUserIds: mentionedUserIds.map(id => Number(id)).filter(id => !isNaN(id))
              } as PatchOrderCommunictionDto
            }));
    }
    public sendMessage(): void {
      
      const newMessage = this.messageText;
      if (!newMessage || !newMessage.trim()) {
        return;
      }
  
      const timestamp = new Date();
      const mentionedUserIds = extractMentionedUserIds(newMessage);

      const message: MentionMessage = {
        id: this.nextMessageId(),
        text: newMessage,
        author: this.getCurrentUser(),
        timestamp: timestamp,
        mentionedUserIds: mentionedUserIds
      };
      this._communications = [...this._communications, message];
  
      this.store.dispatch(new RequestActions.PostCommunication({
              key: this.draftId,
              communication: {
              userId: CURRENT_USER_ID,
              message: newMessage,
              mentionedUserIds: mentionedUserIds.map(id => Number(id)).filter(id => !isNaN(id))
              } as PatchOrderCommunictionDto
            }));
            
      this.messageText = '';
      this.closeMentionDropdown();
    }
    public getCurrentUser(): User {
      return {id: CURRENT_USER_ID, name: `You` }
    }
    ngOnInit(): void {
      const initial = this._initialcommunications || [];
      this.messageId = initial.reduce((max, item) => {
        return typeof item.id === 'number' && item.id > max ? item.id : max;
      }, 0);

      this._communications = initial.map((x: ORD_Communication): Message => {
        return {
          id: x.id ?? this.nextMessageId(),
          author: { id: x.userId, name: x.userDisplayName },
          text: x.message,
          timestamp: x.timestamp ? new Date(x.timestamp) : new Date()
        };
      });

      // Load users for @mention dropdown
      this.users$.pipe(takeUntil(this.destroy$)).subscribe(users => {
        this.allChatUsers = users.map(u => ({
          id: String(u.id),
          name: u.name
        }));
      });

      this.initializeSignalR();
    }

    /**
     * Initialize SignalR connection and subscribe to real-time chat events
     */
    private initializeSignalR(): void {
      this.signalRService.startConnection().then(() => {
        console.log('CommunicationData: SignalR connected');

        // Join the order group so we receive messages for this order
        if (this.draftId) {
          this.signalRService.joinOrderGroup(this.draftId);
        }

        // Subscribe to new communication messages
        this.signalRService.onCommunicationPosted
          .pipe(takeUntil(this.destroy$))
          .subscribe(event => {
            if (event && event.orderId === this.draftId && event.userId !== this.currentUserId) {
              console.log('New message received via SignalR:', event);
              const message: Message = {
                id: event.messageId ?? this.nextMessageId(),
                author: { id: event.userId, name: event.userName },
                text: event.message,
                timestamp: event.timestamp ? new Date(event.timestamp) : new Date()
              };
              this._communications = [...this._communications, message];
            }
          });
      }).catch(err => {
        console.error('CommunicationData: SignalR connection failed', err);
      });
    }

    ngOnDestroy(): void {
      // Leave the order group and clean up
      if (this.draftId) {
        this.signalRService.leaveOrderGroup(this.draftId);
      }
      this.destroy$.next();
      this.destroy$.complete();
    }

    private nextMessageId(): number {
      this.messageId += 1;
      return this.messageId;
    }
}
